import React, { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import { api } from "../axios/api";
import { numberFormat } from "../config/Config";
import {
  Users, UserPlus, UserCheck, UserMinus, 
  DollarSign, ShoppingBag, BarChart3, MapPin, Calendar
} from "lucide-react";
import Loader from "../components/Loader";

const DashboardClientes = () => {
  const user = useSelector((state) => state.user.user);
  const empresaId = user?.empresa_id;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periodoAtivo, setPeriodoAtivo] = useState("mensal");

  useEffect(() => {
    const fetchClientData = async () => {
      if (!empresaId) return;
      try {
        setLoading(true);
        const res = await api.get(`/app/dashboard/clientes/${empresaId}`);
        setData(res.data);
      } catch (err) {
        console.error("Erro ao buscar dados do painel de clientes:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchClientData();
  }, [empresaId]);

  const { cadastros = {}, comportamento = {}, graficos = {}, recentes = {} } = data || {};
  const metricasAtivas = comportamento[periodoAtivo] || { receita: 0, ticketMedio: 0, frequencia: 0, ativos: 0 };

  // Configuração do Gráfico: Crescimento de Novos Clientes
  const customerGrowthOptions = {
    chart: { toolbar: { show: false }, fontFamily: 'Inter, system-ui, sans-serif' },
    stroke: { curve: 'smooth', width: 3 },
    dataLabels: { enabled: false },
    xaxis: { categories: graficos.novosClientesPorMes?.map(c => c.mes) || [], axisBorder: { show: false } },
    colors: ["#10b981"],
    grid: { borderColor: "#f1f5f9", strokeDashArray: 4 }
  };

  // Configuração do Gráfico: Clientes por Região
  const regionChartOptions = {
    chart: { fontFamily: 'Inter, system-ui, sans-serif' },
    labels: graficos.clientesPorRegiao?.map(r => r.regiao) || ["Não Informado"],
    legend: { position: 'bottom', fontSize: '12px' },
    colors: ["#3b82f6", "#10b981", "#f59e0b", "#7c3aed", "#ec4899"],
    plotOptions: { pie: { donut: { size: '70%' } } }
  };

  if (loading) return <Loader />;

  return (
    <div id="layout-wrapper" className="dashboard-clientes">
      <Navbar />
      <Sidebar />

      <style>{`
        .dashboard-clientes { background-color: #f8fafc; min-height: 100vh; }
        .app-wrapper { padding: 24px; }
        .metric-card { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 20px; transition: all 0.2s ease; }
        .metric-card:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.03); }
        .period-btn { padding: 6px 16px; font-size: 13px; font-weight: 600; border-radius: 6px; border: 1px solid #e2e8f0; background: #fff; color: #64748b; transition: all 0.2s; }
        .period-btn.active { background: #10b981; color: #fff; border-color: #10b981; }
        .card-title-flat { font-size: 13px; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: 0.5px; }
        .table-clean th { font-size: 11px; color: #64748b; text-transform: uppercase; padding: 12px 16px; background: #f8fafc; border-bottom: 1px solid #e2e8f0; }
        .table-clean td { padding: 14px 16px; font-size: 13px; color: #334155; border-bottom: 1px solid #f1f5f9; }
      `}</style>

      <main className="app-wrapper">
        <div className="container-fluid">
          
          {/* Header */}
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
            <div>
              <h2 className="fw-bold text-slate-800 m-0" style={{ fontSize: "26px" }}>Indicadores de Clientes (CRM)</h2>
              <small className="text-muted">Análise de comportamento de compra, retenção e ticket médio.</small>
            </div>
            
            <div className="d-flex gap-1 bg-white p-1 rounded border">
              <button onClick={() => setPeriodoAtivo("diario")} className={`period-btn ${periodoAtivo === "diario" ? "active" : ""}`}>Hoje</button>
              <button onClick={() => setPeriodoAtivo("semanal")} className={`period-btn ${periodoAtivo === "semanal" ? "active" : ""}`}>Últimos 7 dias</button>
              <button onClick={() => setPeriodoAtivo("mensal")} className={`period-btn ${periodoAtivo === "mensal" ? "active" : ""}`}>Mês Atual</button>
            </div>
          </div>

          {/* 📊 SEÇÃO 1: COMPORTAMENTO ECONÓMICO DO CLIENTE */}
          <div className="row g-3 mb-4">
            {[
              { label: `Receita do Período`, valor: `${numberFormat(metricasAtivas.receita, 2, ",", ".")} Kz`, icon: <DollarSign size={20} />, color: "#10b981" },
              { label: `Ticket Médio por Venda`, valor: `${numberFormat(metricasAtivas.ticketMedio, 2, ",", ".")} Kz`, icon: <ShoppingBag size={20} />, color: "#3b82f6" },
              { label: `Frequência de Compra`, valor: `${numberFormat(metricasAtivas.frequencia, 2, ",", ".")} ped/cli`, icon: <BarChart3 size={20} />, color: "#7c3aed" },
              { label: `Clientes Ativos no POS`, valor: metricasAtivas.ativos, icon: <UserCheck size={20} />, color: "#f59e0b" },
            ].map((item, i) => (
              <div key={i} className="col-12 col-sm-6 col-xl-3">
                <div className="metric-card" style={{ borderTop: `4px solid ${item.color}` }}>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="text-muted small fw-semibold text-uppercase">{item.label}</span>
                    <div style={{ color: item.color }}>{item.icon}</div>
                  </div>
                  <h3 className="fw-bold m-0 text-slate-700">{item.valor}</h3>
                </div>
              </div>
            ))}
          </div>

          {/* 👥 SEÇÃO 2: CONTADORES DE BASE DE DADOS */}
          <div className="row g-3 mb-4">
            {[
              { label: "Total de Clientes Cadastrados", valor: cadastros.totalClientes, icon: <Users size={18} />, badge: "Base Geral", color: "bg-slate-100 text-slate-600" },
              { label: "Clientes Recorrentes (Compraram)", valor: cadastros.clientesComCompras, icon: <UserCheck size={18} />, badge: "Retenção", color: "bg-success-subtle text-success" },
              { label: "Clientes sem Histórico", valor: cadastros.clientesInativos, icon: <UserMinus size={18} />, badge: "Inativos", color: "bg-danger-subtle text-danger" },
              { label: "Novos Clientes (Mês Vigente)", valor: cadastros.novosClientesMes, icon: <UserPlus size={18} />, badge: "Atração", color: "bg-info-subtle text-info" },
            ].map((card, idx) => (
              <div key={idx} className="col-12 col-sm-6 col-md-3">
                <div className="metric-card bg-white d-flex align-items-center justify-content-between py-3">
                  <div className="d-flex align-items-center gap-3">
                    <div className={`p-2 rounded ${card.color}`}>
                      {card.icon}
                    </div>
                    <div>
                      <span className="text-muted d-block" style={{ fontSize: "11px" }}>{card.label}</span>
                      <span className="fw-bold fs-6 text-slate-800">{card.valor || 0}</span>
                    </div>
                  </div>
                  <span className="badge bg-light text-dark border rounded-sm" style={{ fontSize: '10px' }}>{card.badge}</span>
                </div>
              </div>
            ))}
          </div>

          {/* 📈 SEÇÃO 3: GRÁFICOS DE EVOLUÇÃO E GEOGRAFIA */}
          <div className="row g-4 mb-4">
            <div className="col-xl-8">
              <div className="metric-card h-100">
                <div className="mb-3"><span className="card-title-flat">Taxa de Adesão (Novos Clientes / Mês)</span></div>
                <ReactApexChart type="area" height={300} series={[{ name: "Novos Registos", data: graficos.novosClientesPorMes?.map(c => c.quantidade) || [] }]} options={customerGrowthOptions} />
              </div>
            </div>
            <div className="col-xl-4">
              <div className="metric-card h-100">
                <div className="mb-3"><span className="card-title-flat">Segmentação por Endereço</span></div>
                <div className="d-flex justify-content-center align-items-center h-100">
                  {graficos.clientesPorRegiao && graficos.clientesPorRegiao.length > 0 ? (
                    <ReactApexChart type="donut" width="100%" height={260} series={graficos.clientesPorRegiao.map(r => r.total)} options={regionChartOptions} />
                  ) : (
                    <span className="text-muted small">Sem dados geográficos.</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* 🧾 SEÇÃO 4: RANKING DE CONSUMO E ÚLTIMOS REGISTOS */}
          <div className="row g-4">
            <div className="col-xl-7">
              <div className="metric-card p-0 h-100">
                <div className="p-3 border-bottom d-flex justify-content-between align-items-center">
                  <span className="card-title-flat m-0">Top 5 Clientes de Maior Valor (Faturamento)</span>
                  <Link to="/clientes" className="btn btn-sm btn-light border fw-semibold text-primary" style={{ fontSize: "11px" }}>Ver Carteira</Link>
                </div>
                <div className="table-responsive">
                  <table className="table-clean w-100 m-0">
                    <thead>
                      <tr>
                        <th>Cliente</th>
                        <th>NIF</th>
                        <th>Nº Pedidos</th>
                        <th className="text-end">Total Gasto</th>
                        <th className="text-end">Lucro</th>
                      </tr>
                    </thead>
                    <tbody>
                      {graficos.topClientes?.map((cli, i) => (
                        <tr key={i}>
                          <td className="fw-bold text-slate-700">
                            <span className="text-muted small me-2">#{i+1}</span>
                            {cli.Cliente?.nome || "Cliente Consumidor"}
                          </td>
                          <td>{cli.Cliente?.nif || "999999999"}</td>
                          <td><span className="badge bg-light text-dark border">{cli.total_pedidos} compras</span></td>
                          <td className="text-end fw-bold text-emerald-600">
                            {numberFormat(cli.total_gasto, 2, ",", ".")} Kz
                          </td>
                          <td className="text-end fw-bold text-success">
                            {numberFormat(cli.lucro_total || 0, 2, ",", ".")} Kz
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="col-xl-5">
              <div className="metric-card p-0 h-100">
                <div className="p-3 border-bottom">
                  <span className="card-title-flat m-0">Últimos Clientes Registados</span>
                </div>
                <div className="table-responsive">
                  <table className="table-clean w-100 m-0">
                    <thead>
                      <tr>
                        <th>Nome</th>
                        <th>Contacto</th>
                        <th className="text-end">Operador</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentes.ultimosClientesRegistados && recentes.ultimosClientesRegistados.length > 0 ? (
                        recentes.ultimosClientesRegistados.map((cli, i) => (
                          <tr key={i}>
                            <td>
                              <span className="fw-semibold d-block text-slate-700">{cli.nome}</span>
                              <small className="text-muted" style={{ fontSize: '10px' }}><Calendar size={10} className="me-1"/>{new Date(cli.createdAt).toLocaleDateString()}</small>
                            </td>
                            <td>
                              <span className="text-slate-600 d-block">{cli.telefone || "N/A"}</span>
                            </td>
                            <td className="text-end text-muted" style={{ fontSize: "12px" }}>
                              {cli.User?.nome}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="3" className="text-center text-muted py-4 small">Nenhum cliente cadastrado recentemente.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <Footer />
        </div>
      </main>
    </div>
  );
};

export default DashboardClientes;
